import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

// Mock token analysis data
const mockTokenData = {
  models: [
    {
      name: 'GPT-4 Turbo',
      totalTokens: 1247892,
      promptTokens: 623946,
      completionTokens: 623946,
      cost: 0.015,
      totalCost: 18717.38
    },
    {
      name: 'GPT-3.5 Turbo',
      totalTokens: 987234,
      promptTokens: 493617,
      completionTokens: 493617,
      cost: 0.002,
      totalCost: 1974.47
    },
    {
      name: 'Claude 3 Sonnet',
      totalTokens: 612165,
      promptTokens: 306083,
      completionTokens: 306082,
      cost: 0.003,
      totalCost: 1836.50
    }
  ],
  promptLengths: [
    { bucket: '0-100', count: 2341 },
    { bucket: '100-500', count: 4567 },
    { bucket: '500-1000', count: 2890 },
    { bucket: '1000-2000', count: 1234 },
    { bucket: '2000+', count: 456 }
  ],
  usagePatterns: Array.from({ length: 24 }, (_, hour) => ({
    hour,
    monday: Math.floor(Math.random() * 100),
    tuesday: Math.floor(Math.random() * 100),
    wednesday: Math.floor(Math.random() * 100),
    thursday: Math.floor(Math.random() * 100),
    friday: Math.floor(Math.random() * 100),
    saturday: Math.floor(Math.random() * 50),
    sunday: Math.floor(Math.random() * 50)
  }))
};

// Hook for token analysis data
const useTokenAnalysis = () => ({
  tokenData: mockTokenData,
  loading: false,
  error: null
});

interface TokenBarProps {
  label: string;
  value: number;
  total: number;
  color: string;
}

function TokenBar({ label, value, total, color }: TokenBarProps) {
  const percentage = (value / total) * 100;
  
  const colorMap = {
    blue: '#3b82f6',
    green: '#10b981',
    yellow: '#f59e0b',
    red: '#ef4444'
  };

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span>{label}</span>
        <span className="font-mono">{value.toLocaleString()}</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className="h-2 rounded-full transition-all duration-300"
          style={{
            width: `${percentage}%`,
            backgroundColor: colorMap[color as keyof typeof colorMap] || color
          }}
        />
      </div>
      <div className="text-xs text-muted-foreground text-right">
        {percentage.toFixed(1)}%
      </div>
    </div>
  );
}

interface HistogramProps {
  data: Array<{ bucket: string; count: number }>;
  xLabel: string;
  yLabel: string;
  height: number;
}

function Histogram({ data, xLabel, yLabel, height }: HistogramProps) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 10, right: 10, left: 10, bottom: 20 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis 
          dataKey="bucket" 
          tick={{ fontSize: 10 }}
          stroke="#6b7280"
          label={{ value: xLabel, position: 'insideBottom', offset: -10 }}
        />
        <YAxis 
          tick={{ fontSize: 10 }}
          stroke="#6b7280"
          label={{ value: yLabel, angle: -90, position: 'insideLeft' }}
        />
        <Tooltip 
          contentStyle={{ 
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }}
        />
        <Bar dataKey="count" fill="#3b82f6" />
      </BarChart>
    </ResponsiveContainer>
  );
}

interface HeatMapProps {
  data: Array<{ hour: number; [key: string]: number }>;
  xLabel: string;
  yLabel: string;
  height: number;
}

function HeatMap({ data, xLabel, yLabel, height }: HeatMapProps) {
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const hours = Array.from({ length: 24 }, (_, i) => i);
  
  const getIntensity = (day: string, hour: number) => {
    const dayKey = day.toLowerCase();
    const hourData = data.find(d => d.hour === hour);
    if (!hourData) return 0;
    return hourData[dayKey] || 0;
  };

  const maxValue = Math.max(...data.flatMap(d => 
    days.map(day => d[day.toLowerCase()] || 0)
  ));

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-25 gap-1 text-xs">
        <div></div>
        {hours.map(hour => (
          <div key={hour} className="text-center text-muted-foreground">
            {hour % 6 === 0 ? hour : ''}
          </div>
        ))}
      </div>
      {days.map(day => (
        <div key={day} className="grid grid-cols-25 gap-1">
          <div className="text-xs text-muted-foreground pr-2 text-right">
            {day.slice(0, 3)}
          </div>
          {hours.map(hour => {
            const intensity = getIntensity(day, hour);
            const opacity = intensity / maxValue;
            return (
              <div
                key={`${day}-${hour}`}
                className="w-3 h-3 rounded-sm border border-gray-200"
                style={{
                  backgroundColor: `rgba(59, 130, 246, ${opacity})`,
                }}
                title={`${day} ${hour}:00 - ${intensity} tokens`}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}

const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
};

export function TokenAnalysis() {
  const { tokenData } = useTokenAnalysis();
  
  return (
    <div className="space-y-4">
      {/* Token Usage by Model */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Token Usage by Model</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {tokenData.models.map((model) => (
              <div key={model.name} className="space-y-3 p-4 border rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-sm">{model.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {formatNumber(model.totalTokens)} tokens total
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-sm font-bold">
                      ${model.totalCost.toFixed(2)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      ${model.cost.toFixed(4)}/1K tokens
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <TokenBar
                    label="Prompt Tokens"
                    value={model.promptTokens}
                    total={model.totalTokens}
                    color="blue"
                  />
                  <TokenBar
                    label="Completion Tokens"
                    value={model.completionTokens}
                    total={model.totalTokens}
                    color="green"
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Token Patterns */}
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Prompt Length Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <Histogram
              data={tokenData.promptLengths}
              xLabel="Token Count"
              yLabel="Frequency"
              height={200}
            />
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Token Usage Patterns</CardTitle>
            <div className="text-xs text-muted-foreground">
              Token usage by hour and day of week
            </div>
          </CardHeader>
          <CardContent>
            <HeatMap
              data={tokenData.usagePatterns}
              xLabel="Hour of Day"
              yLabel="Day of Week"
              height={200}
            />
          </CardContent>
        </Card>
      </div>

      {/* Token Efficiency Metrics */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Token Efficiency Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">68%</div>
              <div className="text-sm font-medium">Efficiency Score</div>
              <div className="text-xs text-muted-foreground">
                Prompt to completion ratio
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {formatNumber(45231)}
              </div>
              <div className="text-sm font-medium">Wasted Tokens</div>
              <div className="text-xs text-muted-foreground">
                From errors and retries
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">$147.20</div>
              <div className="text-sm font-medium">Potential Savings</div>
              <div className="text-xs text-muted-foreground">
                Through optimization
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">87%</div>
              <div className="text-sm font-medium">Cache Hit Rate</div>
              <div className="text-xs text-muted-foreground">
                Duplicate request reduction
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}